import React from "react";
import {
  ActivityIndicator,
  FlatList,
  SafeAreaView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import MeetingOption from "../components/MeetingOption";
import { Room } from "../../../../types/types";
import { FeedbackFabButton } from "../../../../components/FeedbackFabButton";
import Illustration2 from "../../../../assets/Illustration2";
import CreateMeetingButton from "../components/CreateMeetingButton";

type ItemVM = {
  room: Room;
  participantsCount: number;
  isSelected: boolean;
  showFullVisual: boolean;
  isDisabled: boolean;
};

type CreateItem = {
  kind: "create";
  id: string;
};

type ListItem = ItemVM | CreateItem;

type Props = {
  title: string;
  subtitle: string;

  loading: boolean;
  errorText: string | null;

  emptyText: string;

  items: ItemVM[];

  onSelectRoom: (room: Room) => void;
  onPressCreateMeeting: () => void;
};

export default function MyMeetingsView({
  loading,
  errorText,
  items,
  onSelectRoom,
  onPressCreateMeeting,
}: Props) {
  const { width } = useWindowDimensions();
  const minCardWidth = 360;
  const columns = Math.max(
    1,
    Math.floor((width - 80 + 24) / (minCardWidth + 24)),
  );
  const listItems: ListItem[] = items.length
    ? [{ kind: "create", id: "create" }, ...items]
    : items;

  return (
    <SafeAreaView style={styles.container}>
      {loading && <ActivityIndicator size="large" style={{ marginTop: 20 }} />}
      {errorText && <Text style={styles.errorText}>{errorText}</Text>}

      <FlatList
        key={`columns-${columns}`}
        numColumns={columns}
        data={listItems}
        keyExtractor={(it) => ("kind" in it ? it.id : it.room.id)}
        contentContainerStyle={styles.listContainer}
        columnWrapperStyle={columns > 1 ? styles.columnWrapper : undefined}
        renderItem={({ item }) =>
          "kind" in item ? (
            <View style={styles.createCard}>
              <CreateMeetingButton onPress={onPressCreateMeeting} />
              <Text style={styles.createCardText}>
                Create a new conversation
              </Text>
            </View>
          ) : (
            <MeetingOption
              room={item.room}
              isSelected={item.isSelected}
              onSelect={() => onSelectRoom(item.room)}
              participantsCount={item.participantsCount}
              showFullVisual={item.showFullVisual}
              isDisabled={item.isDisabled}
              showDisabledState={false}
              disabledActionLabel="Edit Conversation"
            />
          )
        }
        ListEmptyComponent={
          !loading ? (
            <View style={styles.emptyContainer}>
              <Illustration2 width={415} height={362} />
              <Text style={styles.emptyTitle}>All My Conversations</Text>
              <Text style={styles.emptyText}>
                You haven&#39;t created or joined any conversations yet. You can
                start or join one when you feel ready.
              </Text>

              <CreateMeetingButton onPress={onPressCreateMeeting} />
            </View>
          ) : null
        }
      />
      <FeedbackFabButton />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#F6F7F9" },

  listContainer: {
    paddingHorizontal: 40,
    paddingBottom: 140,
    flexGrow: 1,
  },
  columnWrapper: {
    justifyContent: "flex-start",
    gap: 24,
  },

  errorText: { color: "red", textAlign: "center", marginTop: 12 },

  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 40,
    paddingTop: 24,
  },

  illustration: {
    width: 415,
    height: 362,
  },

  emptyTitle: {
    fontSize: 44,
    fontWeight: "700",
    fontFamily: "Lexend",
    color: "#2B2B2F",
    textAlign: "center",
    marginTop: 40,
    marginBottom: 16,
    lineHeight: 50,
  },

  emptyText: {
    fontSize: 22,
    fontWeight: "400",
    fontFamily: "Lexend",
    color: "#2B2B2F",
    textAlign: "center",
    opacity: 0.6,
    lineHeight: 30,
    maxWidth: 800,
  },

  createCard: {
    flex: 1,
    width: "100%",
    maxWidth: 469,
    minHeight: 367,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: "#E2E5EA",
    backgroundColor: "#FFFFFF",
    marginVertical: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  createCardText: {
    marginTop: 16,
    fontFamily: "Lexend",
    fontWeight: "700",
    fontSize: 14,
    lineHeight: 14,
    letterSpacing: 0,
    textAlign: "center",
    textTransform: "uppercase",
    color: "#3A6FB1",
  },

  fab: {
    position: "absolute",
    right: 20,
    bottom: 20,
    backgroundColor: "#3A6FB1",
  },
});
